<template>
    <div class="uploadLinkCard">
        <div class="uploadLinkCard_header">
            <div class="uploadLinkCard_info">
                <div class="uploadLinkCard_titleRow">
                    <div class="uploadLinkCard_status" :class="`uploadLinkCard_status--${item.status}`">
                        {{ getStatusText(item.status) }}
                    </div>
                    <div class="uploadLinkCard_title">{{ item.title }}</div>
                </div>
                <div class="uploadLinkCard_subtitle">{{ item.description }}</div>
            </div>
            <div class="uploadLinkCard_headerRight">
                <div class="uploadLinkCard_actions">
                    <sw-tooltip content="Visualizar">
                        <sw-button
                            icon="earth"
                            circle-icon
                            size="sm"
                            hover-color="blue"
                            @click="$emit('view', item)"
                        />
                    </sw-tooltip>
                    <sw-tooltip content="Editar">
                        <sw-button
                            icon="pencil"
                            circle-icon
                            size="sm"
                            hover-color="orange"
                            @click="$emit('edit', item)"
                        />
                    </sw-tooltip>
                    <sw-tooltip content="Excluir">
                        <sw-button
                            icon="bin"
                            circle-icon
                            size="sm"
                            hover-color="red"
                            @click="$emit('delete', item)"
                        />
                    </sw-tooltip>
                </div>
            </div>
        </div>

        <div class="uploadLinkCard_body">
            <div class="uploadLinkCard_details">
                <div class="uploadLinkCard_detail">
                    <label>Link de Upload:</label>
                    <div class="uploadLinkCard_link">
                        <input
                            :value="item.uploadUrl"
                            readonly
                            class="uploadLinkCard_linkInput"
                            :id="`uploadLink-${item.id}`"
                        />
                        <CopyButton :textToCopy="item.uploadUrl" />
                    </div>
                </div>

                <div class="uploadLinkCard_stats">
                    <div class="uploadLinkCard_stat">
                        <sw-icon name="eye" />
                        <span>{{ item.stats.views }} acessos</span>
                    </div>
                    <div class="uploadLinkCard_stat">
                        <sw-icon name="upload" />
                        <span>{{ item.stats.uploads }} uploads</span>
                    </div>
                    <div v-if="item.acceptedFileTypes && item.acceptedFileTypes.length > 0" class="uploadLinkCard_stat">
                        <sw-icon name="file-document" />
                        <span>{{ item.acceptedFileTypes.length }} tipos aceitos</span>
                    </div>
                    <div class="uploadLinkCard_stat uploadLinkCard_stat--auth" :class="{ '--protected': item.settings?.requireAuth }">
                        <sw-icon :name="item.settings?.requireAuth ? 'shield' : 'users'" />
                        <span>{{ item.settings?.requireAuth ? 'Acesso Protegido' : 'Acesso Público' }}</span>
                    </div>
                    <div class="uploadLinkCard_stat">
                        <sw-icon name="calendar" />
                        <span>Criado em {{ formatDate(item.createdAt) }}</span>
                    </div>
                    <div v-if="item.expiresAt" class="uploadLinkCard_stat">
                        <sw-icon name="clock" />
                        <span>Expira em {{ formatDate(item.expiresAt) }}</span>
                    </div>
                    <div v-if="item.maxFileSize" class="uploadLinkCard_stat">
                        <sw-icon name="database" />
                        <span>Máx: {{ formatFileSize(item.maxFileSize) }}</span>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
    import CopyButton from '@Component/CopyButton.vue';

    import {
        WEB_LINK_STATUS_LABELS
    } from '@/constants';

    const props = defineProps({
        item: {
            type: Object,
            required: true
        }
    });

    const emit = defineEmits(['view', 'edit', 'delete']);

    // Methods
    const getStatusText = (status) => {
        return WEB_LINK_STATUS_LABELS[status] || 'Desconhecido';
    };

    const formatDate = (dateString) => {
        return new Intl.DateTimeFormat('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        }).format(new Date(dateString));
    };

    const formatFileSize = (bytes) => {
        if (!bytes) return '0 B';
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(1024));
        return `${Math.round(bytes / Math.pow(1024, i) * 100) / 100} ${sizes[i]}`;
    };
</script>

<style lang="scss" scoped>
    @import '@/assets/css/mediaScreen.scss';

    /* UploadLink Card Styles */
    .uploadLinkCard {
        background: linear-gradient(135deg, var(--backgroundCard), #ffffff);
        border-radius: 0.875em;
        padding: 1.25em;
        border: 1px solid var(--borderColorLight, #e5e7eb);
        transition: all 0.3s ease;
        box-shadow: var(--boxShadowSm);
        margin-bottom: 0.875em;
        height: 100%;
        display: flex;
        flex-direction: column;

        &:hover {
            transform: translateY(-2px);
            box-shadow: var(--boxShadowLg);
            border-color: var(--colorGreen, #10b981);
        }
    }

    .uploadLinkCard_header {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        margin-bottom: 1.25em;
        padding-bottom: 0.875em;
        border-bottom: 1px solid var(--borderColorLight, #e5e7eb);
    }

    .uploadLinkCard_info {
        flex: 1;
    }

    .uploadLinkCard_titleRow {
        display: flex;
        align-items: center;
        gap: 1em;
        margin-bottom: 0.25em;
    }

    .uploadLinkCard_headerRight {
        display: flex;
        align-items: center;
        gap: 1.25em;
        flex-shrink: 0;
    }

    .uploadLinkCard_title {
        font-size: 1.125em;
        font-weight: 700;
        color: var(--textPrimary);
        margin-bottom: 0;
    }

    .uploadLinkCard_subtitle {
        font-size: 0.875em;
        color: var(--textSecondary);
        line-height: 1.4;
    }

    .uploadLinkCard_status {
        padding: 0.375em 0.875em;
        border-radius: 0.5em;
        font-size: 0.8125em;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.025em;

        &--active {
            background: rgba(16, 185, 129, 0.1);
            color: var(--colorGreen, #10b981);
            border: 1px solid rgba(16, 185, 129, 0.3);
        }

        &--inactive {
            background: rgba(107, 114, 128, 0.1);
            color: var(--colorGray, #6b7280);
            border: 1px solid rgba(107, 114, 128, 0.3);
        }

        &--expired {
            background: rgba(239, 68, 68, 0.1);
            color: var(--colorRed, #ef4444);
            border: 1px solid rgba(239, 68, 68, 0.3);
        }
    }

    .uploadLinkCard_body {
        width: 100%;
        flex: 1;
    }

    .uploadLinkCard_details {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 1.25em;
    }

    .uploadLinkCard_detail {
        label {
            display: block;
            font-size: 0.8125em;
            font-weight: 600;
            color: var(--textSecondary);
            margin-bottom: 0.5em;
            text-transform: uppercase;
            letter-spacing: 0.025em;
        }
    }

    .uploadLinkCard_link {
        display: flex;
        gap: 0.75em;
        align-items: center;
    }

    .uploadLinkCard_linkInput {
        flex: 1;
        padding: 0.625em 1em;
        border: 1px solid var(--borderColorLight, #e5e7eb);
        border-radius: 0.5em;
        background: var(--backgroundInsideBox, #f8fafc);
        color: var(--textPrimary);
        font-size: 0.875em;
        font-family: monospace;

        &:focus {
            outline: none;
            border-color: var(--colorGreen, #10b981);
            box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
        }
    }

    .uploadLinkCard_stats {
        display: flex;
        gap: 1.25em;
        flex-wrap: wrap;
    }

    .uploadLinkCard_stat {
        display: flex;
        align-items: center;
        gap: 0.5em;
        font-size: 0.8125em;
        color: var(--textSecondary);

        .sw-icon {
            font-size: 1em;
            color: var(--colorGreen, #10b981);
        }

        &--auth {
            .sw-icon {
                color: var(--colorGreen, #10b981);
            }

            &.--protected {
                .sw-icon {
                    color: var(--colorOrange, #f59e0b);
                }
            }
        }
    }

    .uploadLinkCard_actions {
        display: flex;
        flex-direction: row;
        gap: 0.5em;
        align-items: center;
        justify-content: flex-end;
        flex-wrap: wrap;
    }

    /* Responsividade - Tablet */
    @include screen_medias(m) {
        .uploadLinkCard {
            padding: 1.125em;
        }

        .uploadLinkCard_header {
            flex-direction: column;
            gap: 1em;
            align-items: stretch;
        }

        .uploadLinkCard_headerRight {
            flex-direction: row;
            justify-content: space-between;
            align-items: center;
            gap: 1em;
        }

        .uploadLinkCard_actions {
            flex-direction: row;
            gap: 0.5em;
            justify-content: flex-end;
            flex-shrink: 0;
        }

        .uploadLinkCard_stats {
            gap: 1em;
            flex-wrap: wrap;
        }
    }

    /* Responsividade - Mobile */
    @include screen_medias(s) {
        .uploadLinkCard {
            padding: 1em;
        }

        .uploadLinkCard_header {
            flex-direction: column;
            gap: 1em;
            align-items: stretch;
        }

        .uploadLinkCard_headerRight {
            flex-direction: column;
            align-items: stretch;
            gap: 0.75em;
        }

        .uploadLinkCard_actions {
            flex-direction: row;
            gap: 0.5em;
            justify-content: center;
            flex-wrap: wrap;
            width: 100%;

            .sw-tooltip {
                flex: 1;
                min-width: 0;

                .sw-button {
                    width: 100%;
                    min-width: 2.5em;
                }
            }
        }

        .uploadLinkCard_stats {
            gap: 0.75em;
            flex-wrap: wrap;
        }

        .uploadLinkCard_stat {
            font-size: 0.75em;
            flex: 1;
            min-width: fit-content;
        }
    }

    /* Responsividade extra para telas muito pequenas */
    @include screen_s {
        .uploadLinkCard_link {
            flex-direction: column;
            gap: 0.5em;
            align-items: stretch;
        }

        .uploadLinkCard_linkInput {
            width: 100%;
        }

        .uploadLinkCard_titleRow {
            flex-direction: column;
            align-items: stretch;
            gap: 0.75em;
            text-align: center;
        }

        .uploadLinkCard_status {
            align-self: center;
            width: fit-content;
            margin: 0 auto;
        }

        .uploadLinkCard_actions {
            gap: 0.375em;

            .sw-tooltip {
                .sw-button {
                    min-width: 3em;
                }
            }
        }
    }
</style>